<template>
  <div class="acesso-de-grupo" :data-testid="`users-acesso-${atributo}`">
    <div class="acesso-de-grupo-header">
      <h4><i class="pi pi-key"></i> {{ t('Acesso') }} {{ grupo }}</h4>
      <button
        type="button"
        class="btn btn-primary btn-lg acesso-de-grupo-button"
        :data-testid="`users-acesso-${atributo}-button`"
        :disabled="carregando"
        @click="abrir"
      >
        <i class="pi pi-sliders-h"></i> {{ t('Configurar acesso') }}
      </button>
    </div>
    <p v-if="erro" class="acesso-de-grupo-error" :data-testid="`users-acesso-${atributo}-error`">{{ erro }}</p>
    <p v-else-if="marcadas.length === 0" class="acesso-de-grupo-summary" :data-testid="`users-acesso-${atributo}-summary`">
      {{ t('Nenhuma opção liberada') }}
    </p>
    <p v-else class="acesso-de-grupo-summary" :data-testid="`users-acesso-${atributo}-summary`">
      <strong>{{ marcadas.length }}</strong> {{ t('liberada(s)') }}: {{ marcadas.join(', ') }}
    </p>
    <AcessoDeGrupoModal
      v-if="opcoes"
      :show="aberto"
      :atributo="atributo"
      :grupo="grupo"
      :opcoes="opcoes"
      :model-value="modelValue"
      @update:model-value="(value: string) => emit('update:modelValue', value)"
      @close="aberto = false"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * AcessoDeGrupo — o container de TODO atributo destravado por grupo (skill `app-groups`).
 *
 * Nada aqui é por atributo: o título é o grupo que destrava ("Acesso MCP", "Acesso
 * DataWarehouse"), o ícone, o botão e os rótulos são fixos, e as opções vêm do serviço que possui o
 * atributo (useRegrasDeGrupos.opcoesDoAtributo → /usuarios/opcoes_de_atributo → /access-catalog).
 * Um atributo novo aparece aqui sem nenhuma linha de front-end.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { useRegrasDeGrupos } from '@/composables/useRegrasDeGrupos';
import AcessoDeGrupoModal from './AcessoDeGrupoModal.vue';
import { opcoesConcedidas, type OpcaoDeAcesso } from './escoposDeAcesso';

const { t } = useI18n();

const props = defineProps<{
  /** O atributo (sem o prefixo custom:) — compõe os data-testid e busca as opções. */
  atributo: string;
  /** O grupo que o destrava neste tenant — é o título. */
  grupo: string;
  /** O valor atual do atributo (escopos separados por espaço). */
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const { opcoesDoAtributo } = useRegrasDeGrupos();

const opcoes = ref<OpcaoDeAcesso[] | null>(null);
const carregando = ref<boolean>(false);
const erro = ref<string>('');
const aberto = ref<boolean>(false);

// O resumo lista as opções liberadas pelo valor atual — o mesmo cálculo do modal.
const marcadas = computed<string[]>(() => {
  if (!opcoes.value) return [];
  const ids = opcoesConcedidas(props.modelValue, opcoes.value);
  return opcoes.value.filter((o) => ids.has(o.id)).map((o) => t(o.rotulo)).sort();
});

let emVoo: Promise<void> | null = null;
const carregar = (): Promise<void> => {
  if (opcoes.value) return Promise.resolve();
  // Um clique durante a carga inicial espera ESSA carga, não desiste.
  if (emVoo) return emVoo;
  carregando.value = true;
  erro.value = '';
  emVoo = opcoesDoAtributo(props.atributo)
    .then((loaded) => { opcoes.value = loaded; })
    .catch((error: unknown) => {
      erro.value = t('Não foi possível carregar as opções de acesso') + ': ' + (error instanceof Error ? error.message : String(error));
      log('AcessoDeGrupo', 'carregar', 'Falha ao carregar as opções', { atributo: props.atributo, error });
    })
    .finally(() => { carregando.value = false; emVoo = null; });
  return emVoo;
};

onMounted(() => { void carregar(); });

const abrir = async (): Promise<void> => {
  await carregar();
  if (opcoes.value) aberto.value = true;
};
</script>

<style scoped>
.acesso-de-grupo {
  background: #f8fafc;
  border: 2px dashed #c7d2fe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.acesso-de-grupo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.acesso-de-grupo-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.acesso-de-grupo-button {
  font-weight: 600;
  padding: 10px 20px;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
}

.acesso-de-grupo-summary,
.acesso-de-grupo-error {
  margin: 8px 0 0 0;
  font-size: 0.875rem;
  color: #555;
}

.acesso-de-grupo-error {
  color: #b91c1c;
}
</style>
